import { useEffect, useRef, useState, useCallback } from 'react';

async function loadPlacesLibrary() {
  if (window.__placesLib) return window.__placesLib;
  await new Promise((resolve) => {
    if (window.google?.maps?.importLibrary) { resolve(); return; }
    const t = setInterval(() => {
      if (window.google?.maps?.importLibrary) { clearInterval(t); resolve(); }
    }, 100);
    setTimeout(() => { clearInterval(t); resolve(); }, 15000);
  });
  if (!window.google?.maps?.importLibrary) return null;
  try {
    const lib = await window.google.maps.importLibrary('places');
    window.__placesLib = lib;
    return lib;
  } catch (e) {
    console.error('[Places] importLibrary error:', e);
    return null;
  }
}

export function useAddressAutocomplete(inputRef, onSelect) {
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  const [dropdownState, setDropdownState] = useState({ suggestions: [], open: false, rect: null });
  const debounceRef = useRef(null);
  const containerRef = useRef(null);
  const sessionTokenRef = useRef(null);
  const libRef = useRef(null);

  useEffect(() => {
    loadPlacesLibrary().then((lib) => {
      if (!lib) return;
      libRef.current = lib;
      if (lib.AutocompleteSessionToken) {
        sessionTokenRef.current = new lib.AutocompleteSessionToken();
      }
    });
  }, []);

  useEffect(() => {
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setDropdownState({ suggestions: [], open: false, rect: null });
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleInput = useCallback((value) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!value || value.length < 3) {
      setDropdownState({ suggestions: [], open: false, rect: null });
      return;
    }

    debounceRef.current = setTimeout(async () => {
      try {
        let lib = libRef.current;
        if (!lib) {
          lib = await loadPlacesLibrary();
          if (!lib) return;
          libRef.current = lib;
        }
        const { AutocompleteSuggestion, AutocompleteSessionToken } = lib;
        if (!AutocompleteSuggestion) return;
        if (!sessionTokenRef.current && AutocompleteSessionToken) {
          sessionTokenRef.current = new AutocompleteSessionToken();
        }

        const request = {
          input: value,
          includedRegionCodes: ['us'],
        };
        if (sessionTokenRef.current) request.sessionToken = sessionTokenRef.current;

        const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

        if (suggestions?.length) {
          // 用 fixed 定位，计算 input 的位置
          const rect = inputRef.current?.getBoundingClientRect() || null;
          setDropdownState({ suggestions: suggestions.slice(0, 5), open: true, rect });
        } else {
          setDropdownState({ suggestions: [], open: false, rect: null });
        }
      } catch (err) {
        console.error('[Places] Fetch error:', err);
        setDropdownState({ suggestions: [], open: false, rect: null });
      }
    }, 300);
  }, [inputRef]);

  const handleSelect = useCallback(async (suggestion) => {
    setDropdownState({ suggestions: [], open: false, rect: null });
    try {
      const lib = libRef.current;
      const place = suggestion.placePrediction.toPlace();
      await place.fetchFields({ fields: ['addressComponents', 'formattedAddress'] });

      let streetNumber = '', route = '', city = '', stateCode = '', zip = '';
      (place.addressComponents || []).forEach(comp => {
        const t = comp.types;
        if (t.includes('street_number'))                        streetNumber = comp.longText;
        if (t.includes('route'))                                route = comp.shortText;
        if (t.includes('locality'))                             city = comp.longText;
        if (t.includes('sublocality_level_1') && !city)         city = comp.longText;
        if (t.includes('neighborhood') && !city)                city = comp.longText;
        if (t.includes('administrative_area_level_2') && !city) city = comp.longText;
        if (t.includes('administrative_area_level_1'))          stateCode = comp.shortText;
        if (t.includes('postal_code'))                          zip = comp.longText;
      });

      const fullAddress = streetNumber && route
        ? `${streetNumber} ${route}`
        : (place.formattedAddress || suggestion.placePrediction?.text?.text || '');

      if (inputRef.current) inputRef.current.value = fullAddress;
      if (lib?.AutocompleteSessionToken) {
        sessionTokenRef.current = new lib.AutocompleteSessionToken();
      }
      onSelectRef.current({ address: fullAddress, city, state: stateCode, zip });
    } catch (err) {
      console.error('[Places] Select error:', err);
    }
  }, [inputRef]);

  const getMainText = (s) =>
    s?.placePrediction?.structuredFormat?.mainText?.text ||
    s?.placePrediction?.text?.text?.toString() || '';

  const getSecondaryText = (s) =>
    s?.placePrediction?.structuredFormat?.secondaryText?.text || '';

  return {
    suggestions: dropdownState.suggestions,
    open: dropdownState.open,
    dropdownRect: dropdownState.rect,
    handleInput,
    handleSelect,
    containerRef,
    getMainText,
    getSecondaryText,
  };
}
